// Hierarchy-preserving sort for the flattened rollup — PURE + tested.
//
// The tree arrives as a depth-first flat list (a subtotal row followed by its
// children). Sorting that list directly would scatter children away from their
// parents and make the parent-equals-sum-of-shown-children invariant unreadable
// even though it still held — so only SIBLINGS move, and each subtotal keeps its
// own children directly beneath it.
//
// The server sorts by cost on the way out; this re-sorts client-side when the
// user picks a different metric column, without a round trip.

import type { GoogleAdsTreeRow } from '../../types/googleAdsReporting';

interface Group {
  row: GoogleAdsTreeRow;
  children: Group[];
}

/** Rebuild the parent/child structure implied by the depth-first `depth` values. */
function collect(
  rows: readonly GoogleAdsTreeRow[],
  from: number,
  depth: number,
): { groups: Group[]; next: number } {
  const groups: Group[] = [];
  let i = from;
  while (i < rows.length && rows[i]!.depth >= depth) {
    if (rows[i]!.depth > depth) break;
    const row = rows[i]!;
    const inner = collect(rows, i + 1, depth + 1);
    groups.push({ row, children: inner.groups });
    i = inner.next;
  }
  return { groups, next: i };
}

export function sortRollupRows(
  rows: readonly GoogleAdsTreeRow[],
  compare: (a: GoogleAdsTreeRow, b: GoogleAdsTreeRow) => number,
): GoogleAdsTreeRow[] {
  if (rows.length === 0) return [];

  const sortGroups = (groups: Group[]): Group[] =>
    [...groups]
      .sort((a, b) => compare(a.row, b.row))
      .map((g) => ({ row: g.row, children: sortGroups(g.children) }));

  const out: GoogleAdsTreeRow[] = [];
  const flatten = (groups: Group[]): void => {
    for (const g of groups) {
      out.push(g.row);
      flatten(g.children);
    }
  };
  flatten(sortGroups(collect(rows, 0, rows[0]!.depth).groups));
  return out;
}
